import axios, { AxiosInstance } from 'axios';
import { Address } from './types';
import { Platform } from 'react-native';

export class GoogleAddressSearch {
  client: AxiosInstance;
  baseURL = 'https://maps.googleapis.com/maps/api/place';
  autocompleteService: any = undefined;
  placeService: any = undefined;

  #initWeb(apiKey: string) {
    const window = (global as any).window;
    const googleScript = window.document.createElement('script');
    googleScript.async = true;
    googleScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initMap`;
    googleScript.type = 'text/javascript';
    const isGoogleScriptAttached = [...window.document.querySelectorAll('script')].some(e => e.src === googleScript.src);
    if (!isGoogleScriptAttached) {
      window.initMap = () => {
        if (window.google) {
          this.autocompleteService = new window.google.maps.places.AutocompleteService();
          this.placeService = new window.google.maps.places.PlacesService(window.document.createElement('div'));
        }
      };
      window.document.head.appendChild(googleScript);
    } else if (window.google) {
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
      this.placeService = new window.google.maps.places.PlacesService(window.document.createElement('div'));
    }
  }

  constructor(readonly apiKey: string) {
    const baseURL = `https://maps.googleapis.com/maps/api/place`;
    this.client = axios.create({ baseURL });
    if (Platform.OS === 'web') {
      this.#initWeb(apiKey);
    }
  }

  static findByType(placeDetails: PlaceDetails[], type: string) {
    return placeDetails.find(place => place.types.includes(type));
  }

  async #requestSearch(input: string): Promise<any> {
    if (Platform.OS === 'web' && this.autocompleteService) {
      return new Promise((resolve, _reject) => {
        this.autocompleteService.getPlacePredictions(
          {
            input,
            language: 'en',
            componentRestrictions: { country: 'us' },
          },
          (data: any[]) => resolve({ predictions: data })
        );
      });
    } else {
      const { data } = await this.client.get('autocomplete/json', {
        params: {
          input,
          components: 'country:us',
          language: 'en',
          type: 'address',
          key: this.apiKey,
          fields: ['formatted_address', 'geometry', 'place_id', 'types'],
        },
      });
      return data;
    }
  }

  async search(input: string): Promise<PlaceSearchResult[]> {
    const data = await this.#requestSearch(input);
    return data.predictions.map((prediction: any) => ({
      id: prediction.place_id,
      description: prediction.description,
    }));
  }

  async #requestDetails(id: string): Promise<any> {
    if (Platform.OS === 'web') {
      return new Promise((resolve, _reject) => {
        this.placeService.getDetails({ placeId: id }, (details: any) => {
          resolve({
            result: {
              ...details,
              geometry: {
                location: {
                  lat: details.geometry.location.lat(),
                  lng: details.geometry.location.lng(),
                },
              },
            },
          });
        });
      });
    } else {
      const { data } = await this.client.get('details/json', {
        params: { place_id: id, key: this.apiKey },
      });
      return data;
    }
  }

  async getDetails(id: string): Promise<Address> {
    const data = await this.#requestDetails(id);

    const { result } = data;

    const postalCode = GoogleAddressSearch.findByType(result.address_components, 'postal_code');
    const country = GoogleAddressSearch.findByType(result.address_components, 'country');
    const state = GoogleAddressSearch.findByType(result.address_components, 'administrative_area_level_1');

    return {
      text: result.formatted_address,
      lat: result.geometry.location.lat,
      lng: result.geometry.location.lng,
      postalCode: postalCode?.long_name ?? '',
      city: result.vicinity,
      state: state?.long_name ?? '',
      country: country?.long_name ?? '',
    };
  }
}

export interface PlaceSearchResult {
  description: string;
  id: string;
}

interface PlaceDetails {
  long_name: string;
  short_name: string;
  types: string;
}
